import React, { useState, MouseEvent, Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { Download, Calendar, RefreshCw, ClipboardCheck } from "lucide-react";

import { AppConfig } from "../../../../config/app.config";

import { EWhiteLabelingClient } from "../../../../types/misc.enum";
import { IMBTSFeedbackResponse } from "../../../../types/entities.dto";

import { useSaveFeedback } from "../../../../hooks/mbts-ux";

import { ConsentLock } from '../ConsentLock';

import { getWhiteLabelingClient } from "../../../../helpers/misc.helper";

interface AnalysisFeedbackProps {
    scenarioId: string | null;
    analysisId: number;
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};
export const AnalysisFeedback = (props: AnalysisFeedbackProps) => {
    const { scenarioId, analysisId, isConsentLocked, openConsentDataGather } = props;

    const navigate = useNavigate();

    const questions: { key: string; title: string; legendStart: string; legendEnd: string; }[] = [
        {
            key: 'insight_clarity',
            title: 'How clear and actionable were the insights provided in this report?',
            legendStart: 'Not Clear',
            legendEnd: 'Very Clear',
        },
        {
            key: 'visualization_helpfulness',
            title: 'How well did the visual data representations help you understand your performance?',
            legendStart: 'Not Helpful',
            legendEnd: 'Very Helpful',
        },
        {
            key: 'recommendation_likelihood',
            title: 'How likely are you to recommend this assessment to colleagues?',
            legendStart: 'Not Likely',
            legendEnd: 'Very Likely',
        },
    ];

    const [responses, setResponses] = useState<IMBTSFeedbackResponse[]>([]);
    const [improvement, setImprovement] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const { mutate: save } = useSaveFeedback();

    const isSubmitDisabled = () => {
        return isProcessing || isSuccess || responses.length !== questions.length;
    };

    const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (isSubmitDisabled()) return;

        save({
            payload: {
                analysis_id: analysisId,
                responses,
                improvement: improvement.length ? improvement : null,
            },
            setIsProcessing,
            setIsSuccess,
        });
    };

    const handleOpenCalender = () => {
        if (getWhiteLabelingClient() === EWhiteLabelingClient.RIDLEY_CONSULTING || AppConfig.app.isDemo) {
            return window.open('https://calendly.com/bethridley1/30-minute-meeting-clone-1', '_blank');
        };

        window.open('https://calendly.com/mindshield/30min', '_blank');
    };

    return (
        <div className='mbts_analysis__feedback'>
            <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />
            <div className="mbts_analysis__feedback__questions">
                <p className='text-sm tracking-wider text-bold mb-2xl'>YOUR FEEDBACK</p>
                {!isSuccess &&
                    <Fragment>
                        <div className="mbts_analysis__feedback__questions__list">
                            {questions.map((que, idx) => (
                                <div key={que.key} className="mbts_analysis__feedback__question">
                                    <p className="text-sm text-semibold">{idx + 1}. {que.title}</p>
                                    <div className="mbts_analysis__feedback__question__radio_btns">
                                        {Array.from({ length: 5 }).map((_, idx) => {
                                            const qIdx = responses.findIndex(r => r.question === que.title);

                                            return (
                                                <label
                                                    key={`${que.key}_${idx}`}
                                                    htmlFor={`${que.key}_${idx}`}
                                                    className="mbts_analysis__feedback__question__radio_btns__btn"
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`feedback_radio--${que.key}`}
                                                        id={`${que.key}_${idx}`}
                                                        checked={qIdx !== -1 && responses[qIdx].rating === idx + 1}
                                                        onChange={() => setResponses(prev => {
                                                            let newList = [...prev];
                                                            if (qIdx !== -1) {
                                                                newList[qIdx].rating = idx + 1;
                                                            } else {
                                                                newList.push({ question: que.title, rating: idx + 1 });
                                                            };
                                                            return newList;
                                                        })}
                                                    />
                                                    <p>{idx + 1}</p>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <div className="mbts_analysis__feedback__question__radio_legend">
                                        <p>{que.legendStart}</p>
                                        <p>{que.legendEnd}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mbts_analysis__feedback__questions__improvement">
                            <p className="text-sm text-medium color--gray-600 mb-xs">How could we improve this experience? (optional)</p>
                            <textarea
                                rows={4}
                                placeholder="Share anything that felt unclear, missing, or could work better."
                                value={improvement}
                                onChange={(e) => setImprovement(e.target.value)}
                            />
                        </div>
                        <button
                            className='btn btn--fluid btn--md btn--radius-md btn--primary'
                            disabled={isSubmitDisabled()}
                            onClick={onSubmit}
                        >
                            {isProcessing && <span className='btn-loader' />}
                            {isProcessing ? <span>Submitting..</span> : <span>Submit Feedback</span>}
                        </button>
                    </Fragment>
                }
                {isSuccess &&
                    <div className="mbts_analysis__feedback__success">
                        <ClipboardCheck />
                        <p>Thanks for your feedback!</p>
                        <p>Your feedback has been recorded successfully.</p>
                    </div>
                }
            </div>
            <div className="mbts_analysis__feedback__actions">
                <p className='text-sm tracking-wider text-bold mb-2xl'>NEXT STEPS</p>
                <div className="mbts_analysis__feedback__action_btns">
                    <button
                        className="mbts_analysis__feedback__action_btns__btn mbts_analysis__feedback__action_btns__btn--download"
                        onClick={() => window.print()}
                    >
                        <div className="mbts_analysis__feedback__action_btns__btn__icon">
                            <Download />
                        </div>
                        <div className="mbts_analysis__feedback__action_btns__btn__right">
                            <p className="text-sm text-bold color--white">Download Report</p>
                            <p className="text-sm color--white">Save a PDF copy of your performance report for your records or to share with your team.</p>
                        </div>
                    </button>
                    <button
                        className="mbts_analysis__feedback__action_btns__btn mbts_analysis__feedback__action_btns__btn--retake"
                        onClick={() => getWhiteLabelingClient() === EWhiteLabelingClient.RIDLEY_CONSULTING ? null : navigate(`/mbts/${scenarioId}/personas`, { replace: true })}
                        disabled={getWhiteLabelingClient() === EWhiteLabelingClient.RIDLEY_CONSULTING}
                    >
                        <div className="mbts_analysis__feedback__action_btns__btn__icon">
                            <RefreshCw />
                        </div>
                        <div className="mbts_analysis__feedback__action_btns__btn__right">
                            <p className="text-sm text-bold color--primary">Retake Assessment</p>
                            <p className="text-sm color--primary">Explore the simulation again to deepen your insights and track your progress over time.</p>
                        </div>
                    </button>
                    <button
                        className="mbts_analysis__feedback__action_btns__btn mbts_analysis__feedback__action_btns__btn--book"
                        onClick={handleOpenCalender}
                    >
                        <div className="mbts_analysis__feedback__action_btns__btn__icon">
                            <Calendar />
                        </div>
                        <div className="mbts_analysis__feedback__action_btns__btn__right">
                            <p className="text-sm text-bold color--primary">Book a Call</p>
                            <p className="text-sm color--primary">Schedule an exploratory conversation to bring curated leadership simulations to your organization.</p>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};